import { OfferModel } from '@/types/api-models'
import { Car, CarCategory, FuelType, TransmissionType } from '@/types/car'

// ─── THW deduplication ────────────────────────────────────────────────────────

/** Código del extra THW en el sistema Rentway (Theft Harm Waiver / robo total) */
const THW_CODE = 'THW'

/**
 * Retorna true si la oferta es la variante con el paquete THW.
 *
 * Estrategia de detección (en orden de fiabilidad):
 * 1. Campo `package` = "THW" → el más explícito
 * 2. `extrasIncluded` CSV contiene "THW"
 * 3. `allExtras` tiene un extra THW marcado como `extra_Required: true`
 *    (cubre casos como Teramont donde THW no aparece en extrasIncluded)
 */
function isThwVariant(offer: OfferModel): boolean {
	// Check primario: campo package
	if (offer.package?.toUpperCase() === THW_CODE) return true
	// Fallback: extrasIncluded CSV
	if (offer.extrasIncluded) {
		const codes = offer.extrasIncluded
			.split(',')
			.map(c => c.trim().toUpperCase())
		if (codes.includes(THW_CODE)) return true
	}
	// Fallback: allExtras con THW obligatorio
	if (offer.allExtras) {
		return offer.allExtras.some(
			e => e.extraID?.toUpperCase() === THW_CODE && e.extra_Required === true,
		)
	}
	return false
}

/**
 * Elimina duplicados causados por la variante THW del sistema Rentway.
 *
 * El API devuelve el mismo vehículo dos veces cuando THW está disponible.
 * Ambas variantes comparten el mismo `groupID`; la diferencia está en:
 *   - oferta base: `package: ""`
 *   - oferta THW:  `package: "THW"` (+ THW en extrasIncluded o allExtras)
 *
 * Esta función agrupa por `groupID` y, cuando detecta un par (base + THW),
 * mantiene sólo la oferta base pero adjunta la oferta THW como `_twhOffer`
 * para uso posterior en el Step 2 y Step 3 de la reserva.
 *
 * Los vehículos sin duplicado THW se devuelven sin cambios.
 */
export function deduplicateTwhOffers(
	offers: OfferModel[],
): Array<OfferModel & { _twhOffer?: OfferModel }> {
	// Agrupar por groupID (ambas variantes comparten el mismo groupID)
	const groups = new Map<string, OfferModel[]>()

	for (const offer of offers) {
		const key = offer.groupID
		if (!groups.has(key)) groups.set(key, [])
		groups.get(key)!.push(offer)
	}

	const result: Array<OfferModel & { _twhOffer?: OfferModel }> = []

	for (const [, group] of groups) {
		if (group.length >= 2) {
			const base = group.find(o => !isThwVariant(o))
			const thw = group.find(o => isThwVariant(o))
			if (base && thw) {
				// Fusionar: conservar la oferta base y adjuntar la variante THW
				result.push({ ...base, _twhOffer: thw })
				continue
			}
		}
		// Sin par THW detectado → agregar todas las ofertas del grupo
		for (const offer of group) {
			result.push(offer)
		}
	}

	return result
}

// ─── Mapper principal ─────────────────────────────────────────────────────────

/**
 * Maps an OfferModel from the API to a Car object
 * Preserves allExtras in the Car object for later access
 */
export function mapVehicleToCar(
	offer: OfferModel & { _twhOffer?: OfferModel },
): Car {
	// Determine transmission: use API field first, fallback to SIPP code (3rd char: A=automatic, M=manual)
	const sippTransmission =
		offer.sipp?.length >= 3 ? offer.sipp[2] === 'A' : undefined
	const transmission: TransmissionType =
		offer.groupDetail.automaticTransmission || sippTransmission
			? 'automatic'
			: 'manual'

	const fuelTypeMap: Record<string, FuelType> = {
		GAS: 'gasoline',
		Gasolina: 'gasoline',
		Diesel: 'diesel',
		DIESEL: 'diesel',
		Híbrido: 'hybrid',
		HYBRID: 'hybrid',
		Eléctrico: 'electric',
		ELECTRIC: 'electric',
	}

	const fuelType: FuelType =
		fuelTypeMap[offer.groupDetail.fuel] ||
		(offer.groupDetail.fuel?.toLowerCase() as FuelType) ||
		'gasoline'

	// Map category based on API category field and SIPP code
	let category: CarCategory = 'midsize' // Default

	// Primary mapping: use groupDetail.category from API
	const apiCategory = offer.groupDetail.category
	if (apiCategory) {
		const categoryMap: Record<string, CarCategory> = {
			HB: 'economy', // Hatchback
			CO: 'compact', // Compact
			SE: 'midsize', // Sedan
			ST: 'fullsize', // Standard
			'4X4': 'suv', // SUV/4x4
			SUV: 'suv',
			VAN: 'van',
			PK: 'pickup',
			LUX: 'luxury',
		}
		category = categoryMap[apiCategory] || 'midsize'
	} else {
		// Fallback to SIPP code logic
		const sippCode = offer.sipp || offer.groupID
		if (sippCode && sippCode.length >= 2) {
			const firstLetter = sippCode[0]
			const secondLetter = sippCode[1]

			if (secondLetter === 'P') {
				category = 'pickup'
			} else if (secondLetter === 'V') {
				category = 'van'
			} else if (secondLetter === 'F' || secondLetter === 'S') {
				category = 'suv'
			} else if (firstLetter === 'E' || firstLetter === 'M') {
				category = 'economy'
			} else if (firstLetter === 'C') {
				category = 'compact'
			}
		}
	}

	const brand = offer.groupDetail.brand || 'Unknown'
	const model = offer.groupDetail.model || 'Unknown Model'

	// Create the Car object and preserve allExtras for later use
	const car: Car & { allExtras?: typeof offer.allExtras } = {
		id: offer.groupID || 'unknown-id',
		groupID: offer.groupID || 'unknown-group',
		brand,
		model,
		year: new Date().getFullYear(), // Not provided by API
		category,
		images: [
			{
				id: '1',
				url: offer.imageURL || offer.groupDetail.imageURL || '',
				alt: `${brand} ${model}`,
				isPrimary: true,
				order: 1,
			},
		],
		specifications: {
			seats: offer.groupDetail.seats || 5,
			doors: offer.groupDetail.doors || 4,
			luggage: offer.groupDetail.suitcases || 2,
			transmission,
			gears: offer.groupDetail.gears,
			fuelType,
			fuelConsumption: '',
			engineDisplacement: offer.groupDetail.engineDisplacement,
			powerHP: offer.groupDetail.powerHP,
			torqueNm: offer.groupDetail.torqueNm,
		},
		features: {
			airConditioning: offer.groupDetail.airConditioning ?? true, // Default true
			gps: false,
			bluetooth: true,
			usbPort: true,
			backupCamera: false,
			cruiseControl: false,
			sunroof: false,
			heatedSeats: false,
		},
		pricing: {
			dailyRate: offer.totalDayValueWithTax || offer.dayValue || 0,
			weeklyRate: offer.previewValueWithDiscount || offer.previewValue,
			currency: offer.currency || 'COP',
			deposit: 0,
			insurance: 0,
		},
		rating: 0,
		reviewCount: 0,
		availability: offer.available ?? true,
		location: offer.station || '',
		description: offer.group_Name || '',
		createdAt: new Date(),
		updatedAt: new Date(),
		originalOffer: offer,
	}

	// Preserve allExtras from OfferModel for use in AdditionalServicesStep
	if (offer.allExtras && offer.allExtras.length > 0) {
		car.allExtras = offer.allExtras
	}

	// Adjuntar la variante TWH si existe (resultado de deduplicateTwhOffers)
	if (offer._twhOffer) {
		car.twhOffer = offer._twhOffer
	}

	return car
}
